import React, { Suspense, lazy, useEffect } from 'react';
import { useLayout } from 'hooks/layout';
import { useModalStoreController } from 'store/Modal';
import { Layout } from 'components/Layout';
import { GlobalStyle } from 'styled';
import Routes from 'pages/Routes';
import { ScrollYPositionProvider } from 'context/ScrollYPositon';
import { ScrollListPositionProvider } from 'context/ScrollListPositon';
import { WebsocketsProvider } from 'context/Websockets';
import { useProfileInit } from 'hooks/profile';
import VersionChecker from 'features/VersionChecker';
import { ToggleConsoleService } from 'store/common/toggle/ConsoleService';
import { useCSI } from 'hooks/useCSI';
import { useInitApp } from 'helpers/initApp';
import useLoggers from 'hooks/useLoggers';
import useAfterRedirectUrlRestore from 'hooks/useAfterRedirectUrlRestore';
import { useInitialRequestsToCache } from './hooks/useInitialRequestsToCache';
import { useDispatchWaffles } from './hooks/useWaffle';
import useBindProfileDI from './core/profile/di/useBindProfileDI';
import { FullScreenSpin } from './components/Spin';

const ModalGallery = lazy(() => import('modals/Common/ModalGallery'));

const SuspenseModalGallery = () => (
  <Suspense fallback={null}>
    <ModalGallery />
  </Suspense>
);

const Root: React.FC = () => {
  useAfterRedirectUrlRestore();
  const { isInitializated } = useProfileInit();
  const layoutProps = useLayout();
  useInitialRequestsToCache();
  useModalStoreController();
  useLoggers();
  useCSI();
  useDispatchWaffles();

  if (!isInitializated) return <FullScreenSpin />;

  return (
    <Layout {...layoutProps}>
      <GlobalStyle />

      <WebsocketsProvider>
        <VersionChecker>
          <ScrollYPositionProvider>
            <ScrollListPositionProvider>
              <Routes />
            </ScrollListPositionProvider>
          </ScrollYPositionProvider>
        </VersionChecker>
      </WebsocketsProvider>

      <SuspenseModalGallery />
    </Layout>
  );
};

const RootWrapper = () => {
  const { isInitializated } = useBindProfileDI();

  useInitApp();

  useEffect(() => {
    (window as any).toggle = new ToggleConsoleService();
  }, []);

  if (!isInitializated) return <FullScreenSpin />;

  return <Root />;
};

export default RootWrapper;
